import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Styled from "../../../CompanyForm.styled";
import {
    resendVerificationCode,
    verifySecurityCodeAndEmail,
    getAssociations,
} from "../../../config/fetchAPI.js";
import { ButtonText, ButtonPurple } from "../../elements/Buttons/Buttons.js";
import CustomAlert from "../../elements/Alert/Alert.js";
import { VerificationTextfield } from "../../elements/TextField/TextField.js";
import logo from "../../../images/horizontal-logo.png";
import { withSupportContact } from "../../../utils/apiErrorUtils";

export const VerificationPage = () => {
    const [codeDigit, setCodeDigit] = useState(["", "", "", "", "", ""]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [timer, setTimer] = useState(30);
    const [isCooldown, setIsCooldown] = useState(false);
    const [isFormCompleted, setIsFormCompleted] = useState(false);
    const navigate = useNavigate();
    const email = sessionStorage.getItem("email");

    // Delete it later
    // get usedUUID from localStorage
    const usedUUID = localStorage.getItem("usedUUID");
    const storedUUID = sessionStorage.getItem("storedUUID");

    // if usedUUID set isFormCompleted to true
    useEffect(() => {
        if (usedUUID === storedUUID) {
            setIsFormCompleted(true);
        }
    }, [usedUUID, storedUUID]);

    useEffect(() => {}, [email]);

    const handleChange = (event, index) => {
        const value = event.target.value;

        // Allow only digits
        if (/[^0-9]/.test(value)) return;

        const newCodeDigit = [...codeDigit];
        newCodeDigit[index] = value;
        setCodeDigit(newCodeDigit);

        if (value && index < 5) {
            document.getElementById(`otp-input-${index + 1}`).focus();
        }

        if (!value && index > 0) {
            document.getElementById(`otp-input-${index - 1}`).focus();
        }
    };

    useEffect(() => {
        let timerInterval;
        if (isCooldown && timer > 0) {
            timerInterval = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);
        } else if (timer === 0) {
            setIsCooldown(false);
        }
        return () => clearInterval(timerInterval);
    }, [isCooldown, timer]);

    const handlePaste = (event) => {
        const pastedValue = event.clipboardData.getData("Text");

        // Ensure that the pasted value is 6 digits
        if (pastedValue.length === 6 && /^[0-9]{6}$/.test(pastedValue)) {
            setCodeDigit(pastedValue.split("")); // Set OTP code as an array of digits
        }
    };

    const handleResendCode = async () => {
        const email = sessionStorage.getItem("email");
        if (!email) {
            setError(withSupportContact("No email found in session storage"));
            return;
        }
        setLoading(true);
        setError("");
        await sendEmail(email);
        setLoading(false);
        setIsCooldown(true);
        setTimer(30);
    };

    const sendEmail = async () => {
        const email = sessionStorage.getItem("email");
        try {
            const result = await resendVerificationCode(email);

            if (
                result &&
                result.message &&
                result.message.includes("OTP Sent")
            ) {
                setSnackMessage("Code has been sent successfully");
                setSnackOpen(true);
            } else {
                setError(withSupportContact("Error Sending OTP"));
            }
        } catch (error) {
            setError(
                withSupportContact(
                    "An unexpected error occurred. Please try again"
                )
            );
        }
    };

    const handleConfirm = async () => {
        const code = codeDigit.join("");
        if (code.length === 6 && /^[0-9]{6}$/.test(code)) {
            try {
                const response = await verifySecurityCodeAndEmail(code);

                if (
                    response &&
                    response.message &&
                    response.message.includes("User authenticated.")
                ) {
                    const token = response.token;
                    if (typeof token === "object") {
                        sessionStorage.setItem("authToken", token.token);
                    } else {
                        sessionStorage.setItem("authToken", token);
                    }
                    sessionStorage.getItem("authToken");

                    // save associations to sessionStorage
                    const associations = await getAssociations();
                    sessionStorage.setItem(
                        "associations",
                        JSON.stringify(associations)
                    );

                    // Proceed to the next page
                    navigate("/companyDetail");
                } else if (response === "completed") {
                    setIsFormCompleted(true);
                }

                if (response && response === "error_400") {
                    setError(withSupportContact("Invalid code"));
                    return;
                }
                if (response && response === "error_401") {
                    setError(withSupportContact("Unauthorised access"));
                    return;
                }

                if (response && response === "error_404") {
                    setError(withSupportContact("Not found"));
                    return;
                }

                if (response && response === "error_500") {
                    setError(withSupportContact("Internal server error"));
                    return;
                }
            } catch (error) {
                setError(
                    withSupportContact(
                        "An unexpected error occurred. Please try again"
                    )
                );
            }
        }
    };
    const handleSnackClose = () => {
        setSnackOpen(false);
    };
    return (
        <Styled.EntryContainer>
            <Styled.EntryBox>
                <Styled.LogoContainer>
                    <Styled.CustomLogo
                        src={logo}
                        alt="Logo"
                    />
                </Styled.LogoContainer>

                {isFormCompleted ? (
                    <Styled.EntryNoteBox>
                        <Styled.SubTitle>
                            Form has been completed.
                        </Styled.SubTitle>
                        <Styled.BodyText>
                            Our team will review your submission and guide you
                            through the onboarding process. If you have any
                            questions, feel free to reach out to our support
                            team at{" "}
                            <Styled.StyledLink href="mailto:employersupport@nla.com.au">
                                employersupport@nla.com.au
                            </Styled.StyledLink>
                            .
                        </Styled.BodyText>
                        <Styled.BodyText
                            style={{
                                fontSize: "20px",
                                fontWeight: "500",
                                fontStyle: "italic",
                            }}>
                            We're excited to partner with you!
                        </Styled.BodyText>
                    </Styled.EntryNoteBox>
                ) : (
                    <>
                        <Styled.MainTitle>2-Step verification</Styled.MainTitle>

                        <Styled.BodyText style={{ fontWeight: "500" }}>
                            Enter the security code that we sent to your email.
                        </Styled.BodyText>

                        <Styled.CodePageWrapper>
                            <Styled.EntryCardBox style={{ width: "100%" }}>
                                <Styled.OTPInputBox
                                    onPaste={handlePaste}
                                    style={{ margin: "0 0 20px 0" }}>
                                    {codeDigit.map((digit, index) => (
                                        <VerificationTextfield
                                            key={index}
                                            id={`otp-input-${index}`}
                                            value={digit}
                                            onChange={(e) =>
                                                handleChange(e, index)
                                            }
                                            variant="outlined"
                                            size="small"
                                            slotProps={{
                                                htmlInput: { maxLength: 1 },
                                            }}
                                        />
                                    ))}
                                </Styled.OTPInputBox>

                                <ButtonPurple onClick={handleConfirm}>
                                    Confirm
                                </ButtonPurple>

                                <ButtonText onClick={handleResendCode}>
                                    {loading
                                        ? "Resending..."
                                        : isCooldown
                                          ? `Resend (${timer}s)`
                                          : "Resend Code"}
                                </ButtonText>

                                {snackOpen && (
                                    <CustomAlert
                                        message={snackMessage}
                                        severity="success"
                                        onClose={handleSnackClose}
                                    />
                                )}
                                {error && (
                                    <CustomAlert
                                        message={error}
                                        severity="error"
                                    />
                                )}
                            </Styled.EntryCardBox>
                        </Styled.CodePageWrapper>
                    </>
                )}
            </Styled.EntryBox>
        </Styled.EntryContainer>
    );
};
